// Comparison data for Max OS Cloud vs competitors
// Used by Comparison.jsx dynamic template and individual vs pages

export const comparisons = {
  zapier: {
    id: "zapier",
    competitorName: "Zapier",
    competitorInitial: "Z",
    route: "/compare/zapier",
    // Hero
    hero: {
      headline: "Max OS Cloud vs Zapier — Which One Actually Works for You?",
      subheadline: "Both automate work. Only one thinks for itself.",
      maxOsTagline: "Autonomous AI worker that thinks, decides, and acts 24/7.",
      competitorTagline: "Trigger-action automation for connecting apps and services.",
    },
    // Feature comparison table (15+ features)
    features: [
      { feature: "Automation type", maxOs: "AI-powered", maxOsDetail: "autonomous decisions", competitor: "Rule-based", competitorDetail: "trigger → action" },
      { feature: "Autonomous operation", maxOs: true, maxOsDetail: "24/7 without triggers", competitor: false, competitorDetail: "trigger-based only" },
      { feature: "AI understanding", maxOs: true, maxOsDetail: "full LLM reasoning", competitor: false, competitorDetail: "no AI" },
      { feature: "Multi-channel", maxOs: true, maxOsDetail: "WhatsApp, Email, Telegram...", competitor: "partial", competitorDetail: "limited channels" },
      { feature: "Natural language commands", maxOs: true, maxOsDetail: "just describe what you want", competitor: false, competitorDetail: "visual builder only" },
      { feature: "Autonomous agents", maxOs: true, maxOsDetail: "proactive workers", competitor: false, competitorDetail: "passive workflows" },
      { feature: "Setup time", maxOs: "5 minutes", maxOsDetail: null, competitor: "30+ minutes", competitorDetail: null },
      { feature: "Learning curve", maxOs: "Low", maxOsDetail: "conversational", competitor: "Medium", competitorDetail: "builder learning" },
      { feature: "Scalability", maxOs: "Auto-scales", maxOsDetail: "AI handles complexity", competitor: "Manual", competitorDetail: "more zaps = more maintenance" },
      { feature: "Maintenance", maxOs: "Self-improving", maxOsDetail: null, competitor: "Manual", competitorDetail: "breaks when APIs change" },
      { feature: "Pricing (starting)", maxOs: "$49/mo", maxOsDetail: null, competitor: "$19.99/mo", competitorDetail: null },
      { feature: "Human-like responses", maxOs: true, maxOsDetail: null, competitor: false, competitorDetail: "robotic" },
      { feature: "Custom agent skills", maxOs: true, maxOsDetail: null, competitor: "partial", competitorDetail: "code steps" },
      { feature: "24/7 monitoring", maxOs: true, maxOsDetail: null, competitor: false, competitorDetail: "event-driven" },
      { feature: "API access", maxOs: true, maxOsDetail: "Business+", competitor: true, competitorDetail: null },
      { feature: "Error recovery", maxOs: "Self-healing", maxOsDetail: "AI retries & adapts", competitor: "Manual", competitorDetail: "zap fails, you fix" },
    ],
    // When to choose Max OS
    whenChooseMaxOs: [
      { title: "True autonomy", description: "Max OS works 24/7 without triggers or manual prompts. It monitors, decides, and acts on its own." },
      { title: "Thinks, doesn't just route", description: "Unlike trigger-action tools, Max OS understands context and makes decisions like a human worker would." },
      { title: "Multi-channel by default", description: "One agent handles WhatsApp, email, Telegram, and more — no separate workflows needed." },
      { title: "5-minute setup", description: "Sign up, connect your channels, and go. No complex workflow builders or Zap configurations." },
      { title: "Self-improving", description: "Max OS learns your business processes and gets better over time. Zaps stay static until you manually update them." },
      { title: "Human-like communication", description: "Max OS writes and responds like a real person — not a robotic automation." },
    ],
    // When Zapier might be enough
    whenChooseCompetitor: [
      { title: "Simple app-to-app connections", description: "If you just need to move data between two SaaS tools, Zapier's simplicity is hard to beat." },
      { title: "Massive app directory", description: "Zapier integrates with 6,000+ apps. If you need a niche integration, Zapier likely has it." },
      { title: "Low complexity workflows", description: "For straightforward if-this-then-that automations, Zapier is quick and effective." },
      { title: "Team familiarity", description: "If your team already knows Zapier and has existing workflows, migration has a cost." },
    ],
    // Verdict
    verdict: {
      summary: "Zapier is great for simple trigger-action workflows between apps. But if you need an autonomous worker that thinks, communicates naturally, and operates 24/7 across channels — Max OS is the next step.",
      recommendation: "Choose Max OS if you want an AI employee that works independently. Choose Zapier if you need simple app connectors.",
      winner: "maxos",
    },
    // Honest note
    honestNote: "Zapier is great for simple trigger-action workflows. Max OS is for when you need an autonomous worker that thinks.",
    // CTA
    cta: {
      primary: "Start Free Trial",
      primaryLink: "/pricing",
      secondary: "Talk to Sales",
      secondaryLink: "/contact",
    },
    // SEO
    seo: {
      title: "Max OS Cloud vs Zapier — Honest Comparison 2026 | Max Employee",
      description: "Honest comparison of Max OS Cloud and Zapier. See how AI-powered autonomous workers compare to rule-based automation across 16 features.",
      url: "/compare/zapier",
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Is Max OS Cloud better than Zapier?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Max OS Cloud and Zapier serve different purposes. Zapier excels at simple trigger-action workflows between apps. Max OS Cloud provides autonomous AI workers that think, decide, and act independently across multiple channels.",
            },
          },
          {
            "@type": "Question",
            name: "Can Max OS Cloud replace Zapier?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "For multi-channel AI-powered automation with natural language understanding, Max OS Cloud can replace many Zapier use cases. However, for simple app-to-app data transfers with 6,000+ integrations, Zapier may still be useful.",
            },
          },
          {
            "@type": "Question",
            name: "How much does Max OS Cloud cost vs Zapier?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Max OS Cloud starts at $49/mo. Zapier's paid plans start at $19.99/mo. Max OS includes AI reasoning, multi-channel communication, and autonomous operation that Zapier doesn't provide.",
            },
          },
        ],
      },
    },
  },

  chatgpt: {
    id: "chatgpt",
    competitorName: "ChatGPT",
    competitorInitial: "C",
    route: "/compare/chatgpt",
    hero: {
      headline: "Max OS Cloud vs ChatGPT — Chat vs Action",
      subheadline: "Both use AI. Only one works while you sleep.",
      maxOsTagline: "Autonomous AI worker that thinks, decides, and acts 24/7.",
      competitorTagline: "Conversational AI that responds when you prompt it.",
    },
    features: [
      { feature: "Autonomous operation", maxOs: true, maxOsDetail: "24/7 without prompts", competitor: false, competitorDetail: "waits for prompts" },
      { feature: "Takes actions", maxOs: true, maxOsDetail: "emails, messages, tasks", competitor: false, competitorDetail: "text only" },
      { feature: "Multi-channel", maxOs: true, maxOsDetail: "WhatsApp, Email, Telegram...", competitor: false, competitorDetail: "web only" },
      { feature: "24/7 monitoring", maxOs: true, maxOsDetail: null, competitor: false, competitorDetail: null },
      { feature: "Proactive behavior", maxOs: true, maxOsDetail: "anticipates needs", competitor: false, competitorDetail: "reactive only" },
      { feature: "Custom knowledge base", maxOs: true, maxOsDetail: "your business data", competitor: "partial", competitorDetail: "Custom GPTs" },
      { feature: "API access", maxOs: true, maxOsDetail: null, competitor: true, competitorDetail: "separate" },
      { feature: "Pricing", maxOs: "$49/mo", maxOsDetail: null, competitor: "$20/mo", competitorDetail: null },
      { feature: "Works while you sleep", maxOs: true, maxOsDetail: null, competitor: false, competitorDetail: null },
      { feature: "Business integrations", maxOs: true, maxOsDetail: "17+ platforms", competitor: "partial", competitorDetail: "plugins" },
      { feature: "Persistent memory", maxOs: true, maxOsDetail: "learns over time", competitor: "partial", competitorDetail: "limited" },
      { feature: "Multi-step reasoning", maxOs: true, maxOsDetail: "complex workflows", competitor: true, competitorDetail: null },
      { feature: "Team collaboration", maxOs: true, maxOsDetail: "shared agents", competitor: false, competitorDetail: "individual" },
      { feature: "Error recovery", maxOs: "Self-healing", maxOsDetail: null, competitor: "Manual", competitorDetail: null },
      { feature: "Voice capabilities", maxOs: true, maxOsDetail: "STT/TTS built-in", competitor: "partial", competitorDetail: "separate" },
      { feature: "Web browsing", maxOs: true, maxOsDetail: "autonomous research", competitor: "partial", competitorDetail: "manual" },
    ],
    whenChooseMaxOs: [
      { title: "Actions, not just answers", description: "Max OS sends emails, messages, and takes real actions. ChatGPT can only tell you what to do." },
      { title: "Always-on worker", description: "Max OS monitors your business 24/7 and acts without being asked. ChatGPT waits for your next prompt." },
      { title: "Multi-channel presence", description: "Max OS works on WhatsApp, email, Telegram, and more. ChatGPT is limited to its web interface." },
      { title: "Business-trained", description: "Max OS learns your specific workflows, customers, and processes — not just general knowledge." },
      { title: "Proactive, not reactive", description: "Max OS anticipates needs and acts before problems arise. ChatGPT only responds when asked." },
      { title: "Your digital employee", description: "Max OS is a worker that operates independently. ChatGPT is an assistant that needs direction." },
    ],
    whenChooseCompetitor: [
      { title: "General knowledge questions", description: "ChatGPT excels at answering questions, brainstorming, and general research across any topic." },
      { title: "Writing and content creation", description: "For drafting documents, emails, or creative writing, ChatGPT is a powerful writing partner." },
      { title: "Learning and education", description: "ChatGPT is excellent for explaining concepts, tutoring, and learning new topics." },
      { title: "Coding assistance", description: "ChatGPT with GPT-4 is a strong coding companion for developers." },
    ],
    verdict: {
      summary: "ChatGPT is an excellent conversational AI assistant. But it's a tool you talk to — not a worker that acts. Max OS takes AI from conversation to action, operating autonomously across your business channels 24/7.",
      recommendation: "Use ChatGPT for research, writing, and brainstorming. Use Max OS when you need an AI employee that takes real actions. Many businesses use both.",
      winner: "maxos",
    },
    honestNote: "ChatGPT is an excellent assistant. Max OS is an employee. Use both together for maximum productivity.",
    cta: {
      primary: "Hire Your AI Worker",
      primaryLink: "/pricing",
      secondary: "Talk to Sales",
      secondaryLink: "/contact",
    },
    seo: {
      title: "Max OS Cloud vs ChatGPT — Chat vs Action | Max Employee",
      description: "Honest comparison of Max OS Cloud and ChatGPT. See how an autonomous AI worker compares to conversational AI across 16 features.",
      url: "/compare/chatgpt",
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Is Max OS Cloud better than ChatGPT?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "They serve different purposes. ChatGPT is a conversational AI for answering questions and generating text. Max OS Cloud is an autonomous AI worker that takes actions across multiple channels 24/7.",
            },
          },
          {
            "@type": "Question",
            name: "Can Max OS Cloud replace ChatGPT?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Max OS Cloud can handle many conversational tasks but is optimized for autonomous business operations. For general knowledge questions and content creation, ChatGPT remains a strong tool.",
            },
          },
          {
            "@type": "Question",
            name: "What can Max OS do that ChatGPT can't?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Max OS can autonomously send emails, messages, monitor your business 24/7, take actions across WhatsApp/Telegram/email, and operate without being prompted. ChatGPT only responds when asked.",
            },
          },
        ],
      },
    },
  },

  make: {
    id: "make",
    competitorName: "Make",
    competitorInitial: "M",
    route: "/compare/make",
    hero: {
      headline: "Max OS Cloud vs Make — Simplicity vs Complexity",
      subheadline: "Both automate workflows. Only one understands what you mean.",
      maxOsTagline: "AI-powered agents that understand natural language and act autonomously.",
      competitorTagline: "Visual automation builder for complex multi-step workflows.",
    },
    features: [
      { feature: "Automation type", maxOs: "AI-powered", maxOsDetail: "natural language", competitor: "Visual builder", competitorDetail: "drag & drop" },
      { feature: "Autonomous operation", maxOs: true, maxOsDetail: "24/7 proactive", competitor: "partial", competitorDetail: "scheduler-based" },
      { feature: "AI understanding", maxOs: true, maxOsDetail: "full LLM reasoning", competitor: false, competitorDetail: "no AI" },
      { feature: "Setup time", maxOs: "5 minutes", maxOsDetail: null, competitor: "1+ hours", competitorDetail: null },
      { feature: "Learning curve", maxOs: "Low", maxOsDetail: "just describe it", competitor: "High", competitorDetail: "complex builder" },
      { feature: "Multi-channel", maxOs: true, maxOsDetail: "WhatsApp, Email, Telegram...", competitor: "partial", competitorDetail: "limited" },
      { feature: "Natural language setup", maxOs: true, maxOsDetail: "describe what you want", competitor: false, competitorDetail: "visual only" },
      { feature: "Maintenance burden", maxOs: "Low", maxOsDetail: "self-improving", competitor: "High", competitorDetail: "manual updates" },
      { feature: "Scalability", maxOs: "Auto-scales", maxOsDetail: null, competitor: "Complex", competitorDetail: "scenario sprawl" },
      { feature: "Pricing (starting)", maxOs: "$49/mo", maxOsDetail: null, competitor: "$9/mo", competitorDetail: null },
      { feature: "Error handling", maxOs: "AI-powered", maxOsDetail: "self-recovery", competitor: "Manual", competitorDetail: "error routes" },
      { feature: "Human-like responses", maxOs: true, maxOsDetail: null, competitor: false, competitorDetail: "robotic" },
      { feature: "Visual workflow builder", maxOs: false, competitor: true, competitorDetail: "advanced" },
      { feature: "App integrations", maxOs: "17+", maxOsDetail: null, competitor: "1,000+", competitorDetail: null },
      { feature: "Real-time adaptation", maxOs: true, maxOsDetail: "AI adjusts on fly", competitor: false, competitorDetail: "static routes" },
      { feature: "Team onboarding", maxOs: "Minutes", maxOsDetail: "no training needed", competitor: "Days", competitorDetail: "training required" },
    ],
    whenChooseMaxOs: [
      { title: "Simplicity wins", description: "Describe what you want in plain language. Max OS handles the logic. No visual builder, no scenario mapping, no complexity." },
      { title: "AI that understands", description: "Max OS uses full LLM reasoning to understand context and intent. Make requires you to define every step explicitly." },
      { title: "5-minute setup vs hours", description: "Get started in 5 minutes. No need to learn a visual builder or map out complex scenario flows." },
      { title: "Self-maintaining", description: "Max OS adapts when things change. Make scenarios break when APIs update and need manual fixing." },
      { title: "Natural communication", description: "Max OS communicates with your customers like a human. Make sends robotic automated messages." },
      { title: "Autonomous operation", description: "Max OS monitors and acts 24/7. Make requires scheduled triggers or webhooks to run." },
    ],
    whenChooseCompetitor: [
      { title: "Complex multi-step workflows", description: "Make's visual builder excels at orchestrating complex sequences across many apps with precise control." },
      { title: "Massive app directory", description: "Make integrates with 1,000+ apps. If you need a niche integration, Make likely supports it." },
      { title: "Data transformation", description: "Make provides powerful data manipulation tools for complex data routing and transformation." },
      { title: "Visual debugging", description: "Make's visual scenario builder makes it easy to see exactly what happens at each step." },
    ],
    verdict: {
      summary: "Make is powerful for complex, multi-step visual workflows — but it comes with complexity, maintenance, and a steep learning curve. Max OS delivers intelligent automation that you simply describe, and it runs autonomously.",
      recommendation: "Choose Max OS if you want simple, intelligent automation that works out of the box. Choose Make if you need granular control over complex multi-app workflows and have the technical resources to maintain them.",
      winner: "maxos",
    },
    honestNote: "Make is powerful for complex visual workflows. Max OS is for when you want intelligent automation without the complexity.",
    cta: {
      primary: "Start Free Trial",
      primaryLink: "/pricing",
      secondary: "Talk to Sales",
      secondaryLink: "/contact",
    },
    seo: {
      title: "Max OS Cloud vs Make (Integromat) — Honest Comparison 2026 | Max Employee",
      description: "Honest comparison of Max OS Cloud and Make (Integromat). See how AI-powered autonomous workers compare to visual automation builders across 16 features.",
      url: "/compare/make",
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Is Max OS Cloud better than Make?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Max OS Cloud and Make serve different needs. Make excels at complex multi-step visual workflows with 1,000+ app integrations. Max OS Cloud provides AI-powered agents that understand natural language and operate autonomously.",
            },
          },
          {
            "@type": "Question",
            name: "Can Max OS Cloud replace Make?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "For intelligent, multi-channel automation with natural language understanding, Max OS Cloud can replace many Make use cases. However, for complex data transformation across 1,000+ apps, Make may still be preferred.",
            },
          },
          {
            "@type": "Question",
            name: "How much does Max OS Cloud cost vs Make?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Max OS Cloud starts at $49/mo. Make's paid plans start at $9/mo. Max OS includes AI reasoning, natural language setup, multi-channel communication, and autonomous operation that Make doesn't provide.",
            },
          },
        ],
      },
    },
  },
};

// Helper to get comparison data by competitor id
export function getComparison(id) {
  return comparisons[id] || null;
}

// Helper to get all comparison routes
export function getAllComparisonIds() {
  return Object.keys(comparisons);
}

export default comparisons;
